import { getExtension } from '@packages/core/utils';
import { browserSupportsHeic } from './browser-support';

const HEIC_CONTENT_TYPES = ['image/heic', 'image/heif'];
const HEIC_EXTENSIONS = ['heic', 'heif'];
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', ...HEIC_EXTENSIONS];

/**
 *  Determines if a file is a renderable image based on its content type or file name.
 *  Considers HEIC/HEIF formats as renderable only if the browser supports them.
 *
 * @param contentType - The MIME type of the file (e.g., "image/jpeg").
 * @param name - The name of the file, used to check the extension if content type is not provided or inconclusive.
 * @returns true if the file is a renderable image, false otherwise.
 */
export function isRenderableImage(contentType?: string, name?: string): boolean {
    if (contentType && isRenderableImageContentType(contentType)) {
        return true;
    }

    if (name && isRenderableImageExtension(name)) {
        return true;
    }

    return false;
}

function isRenderableImageContentType(contentType: string): boolean {
    const normalized = contentType.toLowerCase();

    if (!normalized.startsWith('image/')) {
        return false;
    }

    if (HEIC_CONTENT_TYPES.includes(normalized)) {
        return browserSupportsHeic();
    }

    return true;
}

function isRenderableImageExtension(fileName: string): boolean {
    const extension = getExtension(fileName).toLowerCase();

    if (!IMAGE_EXTENSIONS.includes(extension)) {
        return false;
    }

    if (HEIC_EXTENSIONS.includes(extension)) {
        return browserSupportsHeic();
    }

    return true;
}
